import { useEffect, useMemo, useState } from "react";
import { Upload, X } from "lucide-react";
import { toast } from "sonner";
import { fetchImportSchema, importMappedCsv, parseCsvHeaders } from "@/lib/attendeesApi";

const CORE_DEFAULTS = {
  email: ["email", "e-mail", "work email"],
  name: ["name", "full name", "attendee"],
  mobile: ["mobile", "phone", "tel"],
  company: ["company", "organization", "org"],
  role: ["role", "title", "job title"],
  ticket: ["ticket", "ticket type", "pass"],
  country: ["country", "nation"],
  status: ["status"],
};

function guessColumn(headers, target) {
  const hints = CORE_DEFAULTS[target] || [];
  const lower = headers.map((h) => ({ h, l: h.toLowerCase() }));
  for (const hint of hints) {
    const hit = lower.find((x) => x.l === hint || x.l.includes(hint));
    if (hit) return hit.h;
  }
  return "";
}

export default function CsvImportWizard({ eventId, open, onClose, onSuccess }) {
  const [step, setStep] = useState(1);
  const [csvText, setCsvText] = useState("");
  const [headers, setHeaders] = useState([]);
  const [schema, setSchema] = useState({ coreFields: [], formFields: [] });
  const [mappings, setMappings] = useState({});
  const [concatMode, setConcatMode] = useState({});
  const [busy, setBusy] = useState(false);
  const [defaultStatus, setDefaultStatus] = useState("Invited");

  const targets = useMemo(() => {
    const core = (schema.coreFields || []).map((f) => ({ ...f, kind: "core" }));
    const form = (schema.formFields || []).map((f) => ({
      target: f.id,
      label: f.label,
      required: f.required,
      kind: "form",
    }));
    return [...core, ...form];
  }, [schema]);

  useEffect(() => {
    if (!open) return;
    setStep(1);
    setCsvText("");
    setHeaders([]);
    setMappings({});
    setConcatMode({});
    fetchImportSchema(eventId)
      .then((data) => setSchema(data))
      .catch(() => toast.error("Could not load import schema"));
  }, [open, eventId]);

  const applyFile = (file) => {
    const reader = new FileReader();
    reader.onload = () => {
      const text = String(reader.result || "");
      setCsvText(text);
      const hdrs = parseCsvHeaders(text);
      setHeaders(hdrs);
      const next = {};
      const concat = {};
      for (const t of targets) {
        if (t.kind === "core") {
          next[t.target] = guessColumn(hdrs, t.target);
        } else {
          const labelHit = hdrs.find((h) => h.toLowerCase() === String(t.label).toLowerCase());
          next[t.target] = labelHit || "";
        }
        concat[t.target] = false;
      }
      setMappings(next);
      setConcatMode(concat);
      setStep(2);
    };
    reader.readAsText(file);
  };

  const buildMappingPayload = () => {
    const out = [];
    for (const t of targets) {
      const target = t.target;
      if (concatMode[target]) {
        const sources = (mappings[`${target}__sources`] || "")
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean);
        if (sources.length) {
          out.push({ target, mode: "concat", sources, separator: mappings[`${target}__sep`] || " " });
        }
        continue;
      }
      const source = mappings[target];
      if (source) out.push({ target, mode: "column", source });
      else if (t.required) out.push({ target, mode: "column", source: "" });
    }
    return out;
  };

  const runImport = async () => {
    setBusy(true);
    try {
      const mappingsPayload = buildMappingPayload();
      const data = await importMappedCsv(eventId, {
        csvText,
        mappings: mappingsPayload,
        defaultStatus,
      });
      toast.success(`Imported ${data.insertedOrUpdated ?? 0} row(s)`, {
        description: data.errors?.length ? data.errors.slice(0, 2).join("; ") : undefined,
      });
      onSuccess?.();
      onClose?.();
    } catch (err) {
      toast.error("Import failed", { description: String(err.message || err) });
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" role="dialog" aria-modal="true" aria-label="Import CSV">
      <div className="surface w-full max-w-3xl max-h-[90vh] overflow-y-auto border border-[var(--border-default)]">
        <div className="flex items-center justify-between p-4 border-b border-[var(--border-default)]">
          <div>
            <div className="text-overline">Step {step} of 3</div>
            <h2 className="font-heading text-xl mt-1">Import attendees from CSV</h2>
          </div>
          <button type="button" className="btn-ghost p-2" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </div>

        {step === 1 ? (
          <div className="p-6 space-y-4">
            <p className="text-sm text-[var(--text-secondary)]">
              Upload a CSV file. Required and optional columns follow your published form builder fields.
            </p>
            <label className="flex flex-col items-center justify-center gap-3 border border-dashed border-[var(--border-default)] p-10 cursor-pointer hover:border-[var(--primary)]/50 transition">
              <Upload size={28} className="text-[var(--primary)]" />
              <span className="text-sm font-mono">Choose CSV file</span>
              <input
                type="file"
                accept=".csv,text/csv"
                className="sr-only"
                onChange={(ev) => {
                  const f = ev.target.files?.[0];
                  if (f) applyFile(f);
                }}
              />
            </label>
          </div>
        ) : null}

        {step === 2 ? (
          <div className="p-6 space-y-4">
            <p className="text-xs font-mono text-[var(--text-tertiary)]">
              Detected columns: {headers.join(" · ")}
            </p>
            <label className="text-xs text-[var(--text-secondary)]">
              Default status for new rows
              <select className="input-dark w-full mt-1" value={defaultStatus} onChange={(ev) => setDefaultStatus(ev.target.value)}>
                {["Invited", "Registered", "Paid", "Pending", "Pending Approval"].map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </label>
            <div className="space-y-3 max-h-[50vh] overflow-y-auto pr-1">
              {targets.map((t) => (
                <div key={t.target} className="grid grid-cols-1 md:grid-cols-2 gap-2 items-start border border-[var(--border-default)] p-3">
                  <div className="text-sm">
                    {t.label}
                    {t.required ? <span className="text-[var(--primary)] ml-1">*</span> : null}
                  </div>
                  <div className="space-y-2">
                    <label className="flex items-center gap-2 text-xs font-mono">
                      <input
                        type="checkbox"
                        checked={Boolean(concatMode[t.target])}
                        onChange={(ev) => setConcatMode((m) => ({ ...m, [t.target]: ev.target.checked }))}
                      />
                      Concatenate columns
                    </label>
                    {concatMode[t.target] ? (
                      <>
                        <input
                          className="input-dark text-xs w-full"
                          placeholder="Column names, comma-separated"
                          value={mappings[`${t.target}__sources`] || ""}
                          onChange={(ev) => setMappings((m) => ({ ...m, [`${t.target}__sources`]: ev.target.value }))}
                        />
                        <input
                          className="input-dark text-xs w-full"
                          placeholder="Separator (default space)"
                          value={mappings[`${t.target}__sep`] || ""}
                          onChange={(ev) => setMappings((m) => ({ ...m, [`${t.target}__sep`]: ev.target.value }))}
                        />
                      </>
                    ) : (
                      <select
                        className="input-dark text-xs w-full"
                        value={mappings[t.target] || ""}
                        onChange={(ev) => setMappings((m) => ({ ...m, [t.target]: ev.target.value }))}
                      >
                        <option value="">— Skip —</option>
                        {headers.map((h) => (
                          <option key={h} value={h}>{h}</option>
                        ))}
                      </select>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <div className="flex justify-between gap-2 pt-2">
              <button type="button" className="btn-secondary" onClick={() => setStep(1)}>Back</button>
              <button type="button" className="btn-primary" onClick={() => setStep(3)}>Preview import</button>
            </div>
          </div>
        ) : null}

        {step === 3 ? (
          <div className="p-6 space-y-4">
            <p className="text-sm text-[var(--text-secondary)]">
              {buildMappingPayload().length} field mappings will be applied. Rows with invalid email are skipped.
            </p>
            <div className="flex justify-between gap-2">
              <button type="button" className="btn-secondary" onClick={() => setStep(2)}>Back</button>
              <button type="button" className="btn-primary" disabled={busy} onClick={runImport}>
                {busy ? "Importing…" : "Run import"}
              </button>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
